import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ModalOverlay } from './ModalOverlay'
import { TuCoinIcon } from '../TuCoinIcon'
import { getCharacterImage, type CharacterGender } from '../../assets/character'
import { effectiveCharacterLevel } from '@tfa/shared'
import confetti from 'canvas-confetti'

const F = 'Roboto, sans-serif'

/**
 * #292: поп-ап повышения уровня — с переходом: старый персонаж и цифра уровня
 * сменяются новыми с анимацией (в стиле StreakSuccessModal), затем появляется
 * бейдж заработанных TuCoin. Открывается из Layout по SSE `level.up` или по
 * догоняющей проверке уровня при запуске.
 */
interface Props {
  open: boolean
  onClose: () => void
  fromLevel: number
  toLevel: number
  coins?: number
  gender?: CharacterGender
  /** #355: поднятая внешность персонажа (null — обычная прогрессия). */
  minCharacterLevel?: number | null
}

export function LevelUpModal({ open, onClose, fromLevel, toLevel, coins = 0, gender, minCharacterLevel = null }: Props) {
  // Фаза 'old' — показываем прежний уровень, через паузу переключаемся на 'new'
  // с конфетти: виден сам момент перехода.
  const [phase, setPhase] = useState<'old' | 'new'>('old')

  useEffect(() => {
    if (!open) return
    setPhase('old')
    const t = setTimeout(() => {
      setPhase('new')
      const end = Date.now() + 1600
      const colors = ['#1A3A5C', '#4A6CF7', '#FFD60A', '#34C759', '#AF52DE']
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.6 }, colors })
        confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.6 }, colors })
        if (Date.now() < end) requestAnimationFrame(frame)
      }
      frame()
    }, 900)
    return () => clearTimeout(t)
  }, [open])

  const level = phase === 'new' ? toLevel : fromLevel
  const img = getCharacterImage(effectiveCharacterLevel(level, minCharacterLevel), gender)

  return (
    <ModalOverlay open={open} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: F, gap: 12 }}>
        {/* Персонаж: старый уезжает влево, новый влетает справа с пружиной */}
        <div style={{ width: 150, height: 150, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AnimatePresence mode="popLayout">
            <motion.div
              key={phase}
              initial={phase === 'new' ? { opacity: 0, scale: 0.45, x: 56, rotate: 8 } : false}
              animate={{ opacity: 1, scale: 1, x: 0, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.55, x: -56, rotate: -8 }}
              transition={{ type: 'spring', stiffness: 320, damping: 18 }}
              style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {img ? (
                <img src={img} alt={`Уровень ${level}`} style={{ maxWidth: 150, maxHeight: 150, objectFit: 'contain', filter: phase === 'new' ? 'drop-shadow(0 10px 24px rgba(74,108,247,0.35))' : 'grayscale(0.25) opacity(0.9)' }} />
              ) : (
                <span style={{ fontSize: 84 }}>🏆</span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1C2D41', margin: 0, textAlign: 'center' }}>
          {phase === 'new' ? 'Новый уровень!' : 'Ты прокачался…'}
        </h1>

        {/* Цифра уровня: старая уходит вверх, новая подпрыгивает снизу */}
        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, height: 44, overflow: 'visible' }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: '#6B7B8D' }}>Уровень</span>
          <span style={{ position: 'relative', display: 'inline-flex', width: 52, justifyContent: 'center' }}>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={level}
                data-testid="levelup-number"
                initial={phase === 'new' ? { y: 34, opacity: 0, scale: 0.6 } : false}
                animate={{ y: 0, opacity: 1, scale: phase === 'new' ? [1.35, 1] : 1 }}
                exit={{ y: -34, opacity: 0, scale: 0.6 }}
                transition={{ type: 'spring', stiffness: 380, damping: 16 }}
                style={{
                  fontSize: 40, fontWeight: 800, lineHeight: '44px',
                  background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}
              >
                {level}
              </motion.span>
            </AnimatePresence>
          </span>
        </div>

        {/* TuCoin за уровень — появляется после перехода */}
        <div style={{ minHeight: 28 }}>
          {phase === 'new' && coins > 0 && (
            <motion.span
              data-testid="levelup-coins"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.45, type: 'spring', stiffness: 320, damping: 15 }}
              style={{ height: 28, borderRadius: 14, padding: '0 14px', background: 'rgba(255,149,0,0.14)', color: '#B85C00', fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              +{coins} <TuCoinIcon size={14} />
            </motion.span>
          )}
        </div>

        <button
          data-testid="levelup-close"
          onClick={onClose}
          style={{ width: '100%', height: 52, borderRadius: 16, border: 'none', background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: F, marginTop: 8 }}
        >
          Круто!
        </button>
      </div>
    </ModalOverlay>
  )
}
